import type { AdminGuide } from "../adminGuides.types";
import { GUIDE_SOURCE_DOCS } from "./sourceDocs";

export const accountantReengagementSupportGuide: AdminGuide = {
  id: "accountant-reengagement-support",
  title: "Accountant re-engagement: use Partner Stats segments",
  category: "Customer Support",
  audience: ["Support", "CS"],
  summary:
    "Use Partner Stats segments to contact accountants about first-client setup, pending invites, accepted invites that are not linked yet, unused seats, and active-client follow-up.",
  keywords: [
    "accountant",
    "re-engagement",
    "partner stats",
    "needs attention",
    "invites pending",
    "accepted but not linked",
    "unused seats",
    "support content pack",
    "Email & Comms templates",
    "first client",
    "advisory follow-up",
  ],
  location: "Admin",
  risk: "medium",
  sourceDocs: [
    GUIDE_SOURCE_DOCS.PARTNER_STATS,
    GUIDE_SOURCE_DOCS.ACCOUNTANT_REENGAGEMENT_PLAN,
    GUIDE_SOURCE_DOCS.ACCOUNTANT_REENGAGEMENT_SPRINT_3_SOURCE_LEDGER,
    GUIDE_SOURCE_DOCS.ACCOUNTANT_REENGAGEMENT_SPRINT_3_CONTENT_PACK,
    GUIDE_SOURCE_DOCS.PLAIN_LANGUAGE,
  ],
  warnings: [
    "Do not send real messages from this guide. It is a support-owned content pack and manual-entry aid.",
    "Classify email copy before use. Advisory, rollout, or promotional copy may need the marketing unsubscribe model.",
    "Never paste tokenised invite links, spoof/login URLs, passwords, raw recipient lists, or real PII into reusable templates.",
  ],
  safetyNotes: [
    "Partner Stats segments are current-snapshot triage signals, not a partner health score.",
    "Open the partner profile or support context before contacting the accountant.",
    "Use direct support notes or calls first when consent, unsubscribe, or commercial-message classification is unclear.",
  ],
  whatThisHelpsYouDo: [
    "Turn Partner Stats segments into consistent Support follow-up.",
    "Choose the right first-client setup, invite follow-up, rollout, or advisory-value message.",
    "Prepare draft-only Email & Comms content without sending or publishing it.",
  ],
  whenToUse: [
    "Support is reviewing Partner Stats and sees `Needs attention`, `Invites pending`, `Accepted but not linked`, or `Unused seats`.",
    "Krysi or CS needs consistent copy before contacting accountants about onboarding uptake.",
    "CS needs a copy baseline for an accountant-facing prompt or support CTA.",
  ],
  whenNotToUse: [
    "You need to send a production email or in-app message immediately.",
    "You cannot confirm the Partner Stats segment or source metric.",
    "The message would include tokenised URLs, invite codes, customer lists, secrets, or raw PII.",
    "The request needs Partner Detail activity history; that surface is separate product scope.",
    "The request needs partner roles, partner-dashboard-only access, client-manager assignment, bulk assignment, a health score, setup-for-me work, or Monday automation. Those are separate product scope.",
  ],
  beforeYouStart: [
    "Open **Partner Stats** (`/insights/partner-stats`) and confirm the segment.",
    "If this is a new-partner check, open the Admin user or support context and treat `last_login_at`, `login_count`, `activity_status`, and `usage_last_active_date` as supporting context only.",
    "Confirm whether this is setup support, rollout planning, or advisory-value follow-up.",
    "Check for an existing support ticket or recent contact so the accountant is not contacted repeatedly.",
    "Decide the channel: email, in-app draft, direct support note, or manual call script.",
    "Classify commercial/advisory email before use and preserve sender identity, consent, unsubscribe, and suppression rules.",
  ],
  decisionTable: [
    {
      situation: "First-client setup help",
      use: "Use `Unused seats` plus no visible linked or active client movement. Offer a setup call or written invite steps.",
      notes:
        "Best prompt candidate when the trigger and route can be proven safely.",
    },
    {
      situation: "Client has not accepted",
      use: "Use `Invites pending`. Ask the accountant to send a simple reminder from their relationship.",
      notes: "Do not include invite URLs or invite codes in reusable copy.",
    },
    {
      situation: "Setup accepted but stalled",
      use: "Use `Accepted but not linked`. Ask for non-sensitive setup context and provider.",
      notes:
        "Escalate if linking does not complete after accepted invite evidence.",
    },
    {
      situation: "Next client rollout",
      use: "Use `Unused seats` where linked or active client movement already exists.",
      notes:
        "Likely commercial/advisory if sent by email; classify before use.",
    },
    {
      situation: "Advisory-value follow-up",
      use: "Use `All partners` with linked or active client movement.",
      notes:
        "Not a needs-attention segment. Treat outbound email as advisory/commercial unless confirmed otherwise.",
    },
  ],
  steps: [
    "Start in Partner Stats and select the relevant segment.",
    "Open the partner row or support context before contacting the accountant.",
    "Choose one content item from the content pack. Keep the message factual and tied to the visible segment.",
    "Use placeholders only until you are inside the approved support or messaging tool.",
    "If creating an Email & Comms draft, open `/email-comms/templates`, create a draft, preview it, and leave it unpublished until owner approval.",
    "Do not send a test to a real customer. Use a monitored internal inbox, or a known CRM contact only when intentionally checking marketing unsubscribe compliance.",
    "Record the support outcome in the normal case/ticket context, using counts and IDs only where needed.",
  ],
  checklists: [
    {
      title: "New-partner support checklist",
      items: [
        "I confirmed the account is an accountant/advisor or partner account before using this checklist.",
        "`last_login_at` and `login_count` have been checked, and null, zero, or missing values are treated as unknown or never tracked.",
        "`activity_status` and `usage_last_active_date` have been checked as engagement context, not as proof of first login.",
        "Partner Stats has been checked for `Unused seats`, `Invites pending`, `Accepted but not linked`, linked clients, and active-used seats.",
        "I chose one next step: invite the first client, send written setup steps, or offer a short setup call.",
        "I checked for a recent support ticket or contact so the accountant is not contacted repeatedly.",
      ],
    },
    {
      title: "Before contacting the accountant",
      items: [
        "I confirmed the Partner Stats segment and source metric.",
        "I checked for a current support ticket or recent contact.",
        "The message does not contain tokenised links, invite codes, passwords, raw recipient lists, or real PII.",
        "The channel and commercial-message classification are clear.",
        "The copy has one primary CTA and one assisted-support option.",
      ],
    },
    {
      title: "Draft-only Email & Comms checklist",
      items: [
        "The template is still a draft.",
        "The category matches the approved classification: `transactional` for factual account/setup support, `marketing` for promotional or advisory-value outreach.",
        "The draft has been previewed.",
        "Marketing/unsubscribe behavior is preserved before any future publish or send.",
        "The evidence does not claim a real message was sent.",
      ],
    },
  ],
  whatSuccessLooksLike: [
    "Support can pick a message from the Partner Stats segment without rewriting it from scratch.",
    "The accountant receives one clear next action and one assisted-support path.",
    "CS has a copy baseline, trigger candidate, suppression rule, and blocked-condition list for the first in-app CTA.",
  ],
  troubleshooting: [
    {
      title: "First-client setup help",
      body: [
        "**Target Partner Stats segment:** `Unused seats`, with no visible linked or active client movement.",
        "**Channel:** direct support note or manual call script first; email only when support relationship and consent posture are clear.",
        "**Subject/title:** Help inviting your first client",
        "**Body copy:** Hi [accountant first name], your Partner Dashboard still has client seats available. If you want help getting the first client set up, we can walk through the invite steps and help the client finish setup. Reply with a time that suits you, or tell us whether you would prefer a quick call or written steps.",
        "**Primary CTA:** Reply with a time or ask for written invite steps.",
        "**Secondary option:** Offer a 10-minute setup call.",
        "**Do not use when:** linked or active client movement is already visible, a current support ticket covers the same setup issue, or outbound email classification is unclear.",
        "**Source metric:** `lifetimeRemaining > 0`, `pendingInvites = 0`, `acceptedNotLinkedInvites = 0`, `linkedChildOrgs = 0`, `activeUsed = 0`.",
        "**Owner/escalation:** Support/CS owns outreach. Escalate only if invite creation or partner linking fails.",
      ],
    },
    {
      title: "Invites pending / client has not accepted",
      body: [
        "**Target Partner Stats segment:** `Invites pending`.",
        "**Channel:** direct support note or manual email to the accountant.",
        "**Subject/title:** Client invite still pending",
        "**Body copy:** Hi [accountant first name], it looks like one of your client invites is still waiting to be accepted. A short reminder from you can help because the client recognises your name. If you would like us to help you word the reminder, reply and we will draft it with you.",
        "**Primary CTA:** Send the client a reminder from the accountant relationship.",
        "**Secondary option:** Support drafts a short client reminder for the accountant to send.",
        "**Do not use when:** the invite was just created, has already been accepted, or the accountant was contacted about the same invite within the last 7 days.",
        "**Source metric:** `pendingInvites > 0`.",
        "**Owner/escalation:** Support/CS owns accountant follow-up. Escalate if invite counts look wrong or the accountant cannot find the invite.",
      ],
    },
    {
      title: "Accepted but not linked / setup stalled",
      body: [
        "**Target Partner Stats segment:** `Accepted but not linked`.",
        "**Channel:** direct support note or manual call script.",
        "**Subject/title:** Client setup may be stalled",
        "**Body copy:** Hi [accountant first name], it looks like a client accepted an invite, but the client is not showing as linked in your Partner Dashboard yet. We can help you check the next step. Reply with the client or business name you expected to see, the approximate setup time, and whether they were connecting Xero or QuickBooks. Please do not send passwords, invite links, or login links.",
        "**Primary CTA:** Reply with setup context so Support can check the stalled point.",
        "**Secondary option:** Book a guided setup call if Support owns that handoff.",
        "**Do not use when:** that accepted invite becomes linked, a current support ticket already covers the setup, or the request would expose tokenized URLs.",
        "**Source metric:** `acceptedNotLinkedInvites > 0`.",
        "**Owner/escalation:** Support/CS triages. Escalate with partnerId, approximate timestamps, provider, and non-sensitive context if linking does not complete.",
      ],
    },
    {
      title: "Unused seats / next client rollout planning",
      body: [
        "**Target Partner Stats segment:** `Unused seats`, preferably where linked or active client movement already exists.",
        "**Channel:** manual call script or direct support note first.",
        "**Subject/title:** Plan the next client rollout",
        "**Body copy:** Hi [accountant first name], you still have [remaining seat count] client seats available. If you are ready to bring another client across, we can help you choose the next client and keep the setup simple. Reply if you want help choosing the next client or planning the rollout.",
        "**Primary CTA:** Pick the next client to invite.",
        "**Secondary option:** Support helps choose a first candidate and plan a guided setup.",
        "**Do not use when:** no seats remain, the accountant recently declined rollout help, or an unresolved setup issue blocks the first client.",
        "**Source metric:** `lifetimeRemaining > 0` plus `linkedChildOrgs > 0` or `activeUsed > 0`.",
        "**Compliance:** likely promotional/advisory if sent by email; classify and preserve unsubscribe controls before email use.",
      ],
    },
    {
      title: "Advisory-value follow-up after clients are active",
      body: [
        "**Target Partner Stats segment:** `All partners` filtered by linked or active client movement.",
        "**Channel:** manual call script, direct support note, or later in-app prompt candidate.",
        "**Subject/title:** Next step after your client is active",
        "**Body copy:** Hi [accountant first name], now that your client is active, a useful next step is to open their reports and choose one cashflow question to answer together. If you want a starting point, we can help you find a simple example for the first client conversation.",
        "**Primary CTA:** Ask Support for a first advisory example or walkthrough.",
        "**Secondary option:** Support provides one short example without putting client financial details into reusable templates.",
        "**Do not use when:** the accountant is still blocked on setup or linked/active movement is not visible yet.",
        "**Source metric:** `linkedChildOrgs > 0` or `activeUsed > 0`.",
        "**Compliance:** treat outbound email as commercial/advisory unless compliance confirms a narrower relationship purpose.",
      ],
    },
  ],
  faqs: [
    {
      title: "Why not seed Email & Comms templates yet?",
      body: [
        "Email & Comms can create drafts safely, but seeded templates would still need explicit content ownership, channel classification, unsubscribe/consent decisions, and extra create/update/publish coverage.",
        "Support can use this guide and the content pack to create drafts manually once those ownership decisions are clear.",
      ],
    },
    {
      title: "What should ship first?",
      body: [
        "Recommended candidate: a non-blocking Partner Dashboard CTA for accountants with unused seats and no visible first-client movement.",
        "Copy baseline: Need help inviting your first client? We can walk through the setup with you.",
        "Blocked conditions: role targeting, route ownership, trigger reliability, dismiss/cooldown behavior, and safe support handoff must be verified first.",
      ],
    },
    {
      title: "What is outside this quick-win lane?",
      body: [
        "Do not use this guide to request partner roles, partner-dashboard-only access, client-manager assignment, bulk assignment, health scores, setup-for-me work, or Monday automation.",
        "Those need product owner decisions, permission and data-model work, a larger UX surface, a clear support owner or consumer, and a separate validation plan.",
      ],
    },
  ],
  escalateWhen: [
    "Partner Stats counts conflict with what the accountant can see in Partner Dashboard.",
    "Invite acceptance or linking does not complete after Support has non-sensitive timestamps and provider context.",
    "Support wants to publish or send templates but content ownership, consent, unsubscribe, or classification is unclear.",
    "A requested prompt needs first-login, no-invite-after-48-hours, or activity-history metadata that is not currently wired.",
  ],
  relatedGuides: [
    {
      label: "Partner Portal: invite a client",
      guideId: "partner-portal-invites",
    },
    { label: "Onboarding support", guideId: "onboarding-support" },
    { label: "Send bulk email safely", guideId: "bulk-email" },
  ],
  relatedAdminPaths: [
    { label: "Partner Stats", path: "/insights/partner-stats" },
    { label: "Email & Comms templates", path: "/email-comms/templates" },
    {
      label: "Issue Reports",
      path: "/issue-reports?category=support_ticket",
    },
  ],
};
